/*
  Wishlist (saved pieces).

  The list lives in the browser: localStorage key "cg:wishlist" holds
  { items: [{ handle, variant }], updated }. Newest first, product handles as keys,
  so the wishlist page can ask Shopify for each card by URL.

  Custom elements:
    <wishlist-button data-handle data-variant>  toggles a product; aria-pressed mirrors the state
    <wishlist-count>                             number of saved products (header icon bubble)
    <wishlist-grid data-section-id>              the wishlist page: every saved product is rendered
                                                 by the Section Rendering API (sections/wishlist-card.liquid)

  Every change is announced with a "cg:wishlist" event on document
  (detail: { handle, variant, added, source, element, items }); assets/analytics.js
  turns it into add_to_wishlist / remove_from_wishlist.
*/
(() => {
  const STORAGE_KEY = 'cg:wishlist';
  const MAX_ITEMS = 100;
  const EVENT = 'cg:wishlist';

  function read() {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (data && Array.isArray(data.items)) {
        return data.items
          .filter((item) => item && typeof item.handle === 'string' && item.handle)
          .map((item) => ({ handle: item.handle, variant: item.variant ? String(item.variant) : '' }))
          .slice(0, MAX_ITEMS);
      }
    } catch (error) {
      // Storage blocked or corrupt: start with an empty list, nothing else to do.
    }
    return [];
  }

  let items = read();

  function write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, updated: Date.now() }));
    } catch (error) {
      // Private mode or full storage: the list still works for this page.
    }
  }

  function notify(detail) {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { ...detail, items: items.slice() } }));
  }

  const has = (handle) => items.some((item) => item.handle === handle);

  function toggle(handle, variant = '', source = '', element = null) {
    if (!handle) return false;
    const added = !has(handle);
    items = items.filter((item) => item.handle !== handle);
    if (added) items = [{ handle, variant: variant ? String(variant) : '' }, ...items].slice(0, MAX_ITEMS);
    write();
    notify({ handle, variant, added, source, element });
    return added;
  }

  function remove(handle, { silent = false } = {}) {
    if (!has(handle)) return;
    items = items.filter((item) => item.handle !== handle);
    write();
    if (!silent) notify({ handle, added: false, source: 'sync', element: null });
  }

  // Another tab changed the list: pick it up.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    items = read();
    notify({ external: true });
  });

  window.cosmogenWishlist = { has, toggle, remove, list: () => items.slice() };

  const rootUrl = () => {
    const cartUrl = window.routes?.cart_url || '/cart';
    return cartUrl.replace(/cart\/?$/, '');
  };

  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.onClick = this.onClick.bind(this);
          this.onChange = this.render.bind(this);
          this.button.addEventListener('click', this.onClick);
          document.addEventListener(EVENT, this.onChange);
          this.render();
        }

        disconnectedCallback() {
          this.button?.removeEventListener('click', this.onClick);
          document.removeEventListener(EVENT, this.onChange);
        }

        // On the product page the variant is whatever the customer has picked by now.
        currentVariant() {
          const input = this.closest('product-info')?.querySelector('input.product-variant-id');
          return input?.value || this.dataset.variant || '';
        }

        onClick(event) {
          event.preventDefault();
          event.stopPropagation();
          toggle(this.dataset.handle, this.currentVariant(), this.dataset.source || '', this.button);
        }

        render() {
          const saved = has(this.dataset.handle);
          this.classList.toggle('is-saved', saved);
          this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
          const title = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
          if (title) this.button.setAttribute('aria-label', title);
          const label = this.querySelector('.wishlist-button__label');
          if (label) label.textContent = saved ? this.dataset.labelSaved : this.dataset.labelSave;
        }
      }
    );
  }

  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class WishlistCount extends HTMLElement {
        connectedCallback() {
          this.onChange = this.render.bind(this);
          document.addEventListener(EVENT, this.onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT, this.onChange);
        }

        render() {
          const count = items.length;
          this.textContent = count > 99 ? '99+' : String(count);
          this.hidden = count === 0;
        }
      }
    );
  }

  if (!customElements.get('wishlist-grid')) {
    customElements.define(
      'wishlist-grid',
      class WishlistGrid extends HTMLElement {
        connectedCallback() {
          this.grid = this.querySelector('[data-wishlist-grid]');
          this.empty = this.querySelector('[data-wishlist-empty]');
          this.loading = this.querySelector('[data-wishlist-loading]');
          if (!this.grid) return;
          this.onChange = this.handleChange.bind(this);
          document.addEventListener(EVENT, this.onChange);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENT, this.onChange);
        }

        handleChange({ detail }) {
          if (detail.external) {
            this.render();
            return;
          }
          if (detail.added) return;
          this.grid.querySelector(`[data-handle="${CSS.escape(detail.handle)}"]`)?.remove();
          this.show(this.grid.children.length ? 'grid' : 'empty');
        }

        show(state) {
          this.grid.hidden = state !== 'grid';
          if (this.empty) this.empty.hidden = state !== 'empty';
          if (this.loading) this.loading.hidden = state !== 'loading';
        }

        async fetchCard(handle) {
          const url = `${rootUrl()}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
          try {
            const response = await fetch(url, { headers: { Accept: 'text/html' } });
            if (response.status === 404) return { handle, gone: true };
            if (!response.ok) return { handle };
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const card = html.querySelector('[data-wishlist-card]');
            // No card in the answer: the product exists but is not published to this channel.
            return card ? { handle, card: document.adoptNode(card) } : { handle, gone: true };
          } catch (error) {
            return { handle };
          }
        }

        async render() {
          const list = items.slice();
          if (!list.length) {
            this.grid.replaceChildren();
            this.show('empty');
            return;
          }
          this.show('loading');
          const token = (this.renderToken = Symbol('render'));
          const results = await Promise.all(list.map(({ handle }) => this.fetchCard(handle)));
          if (token !== this.renderToken) return;

          results.filter((result) => result.gone).forEach((result) => remove(result.handle, { silent: true }));
          const cards = results.filter((result) => result.card).map((result) => result.card);
          this.grid.replaceChildren(...cards);
          this.show(cards.length ? 'grid' : 'empty');
        }
      }
    );
  }
})();
